import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import axiosInstance from '../utils/api';
const AuditJobs = () => {
  const [auditJobs, setAuditJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [auditId, setAuditId] = useState(null);
  const [filters, setFilters] = useState({
    JobRef: '',
    Address: '',
    ReasonNames: '',
    LenderName: '',
    AssignedResource: '',
    AuditScore: '',
    SLA: '', 
    Area: '', 

  });


  const region_lookup = {
      '4050':'RMT - North',
      '4070':'RMT - South',
      '4020':'RMT - London & East',
      '45020':'HDG Auditors Team',
      '45017':'HDG Auditors Team',
      '9314':'HDG Auditors Team',
      '9312':'HDG Auditors Team',
      '9304':'HDG Auditors Team',
      '9309':'HDG Auditors Team',
      '9326':'HDG Auditors Team',
      '45021':'HDG Auditors Team',
      '45019':'HDG Auditors Team',
      '45022':'HDG Auditors Team',
      '9314':'HDG Auditors Team',
      '8545':'HDG Auditors Team',
      '8541': 'HDG Auditors Team',
  
  }
  const [loading, setLoading] = useState(true);

  const history = useHistory();

  useEffect(() => {
    const fetchAuditJobs = async () => {
      try {
        const response = await axiosInstance.get('/api/load-audits/');
        if (response.status === 200) {
          setAuditJobs(response.data.data);
          setFilteredJobs(response.data.data); // Initialize filtered jobs with fetched data
        } else {
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchAuditJobs();
  }, []);

  useEffect(() => {
    const filtered = auditJobs.filter((job) => {
      const matchesJobRef = !filters.JobRef || job.JobRef.toLowerCase().includes(filters.JobRef.toLowerCase());
      const address = `${job.Address?.HouseNumber ?? ''} ${job.Address?.HouseName ?? ''} ${job.Address?.Street ?? ''} ${job.Address?.Town ?? ''} ${job.Address?.Postcode ?? ''}`.trim();
      const matchesAddress = !filters.Address || address.toLowerCase().includes(filters.Address.toLowerCase());
      const matchesReasonNames = !filters.ReasonNames || job.ReasonNames.some((reason) =>
        reason.toLowerCase().includes(filters.ReasonNames.toLowerCase())
      );
      const matchesAuditCreation = !filters.AuditCreation || new Date(job.CreatedAt).toLocaleString().toLowerCase().includes(filters.AuditCreation.toLowerCase());
      const matchesAssignedResource = !filters.AssignedResource || `${job.FirstName ?? ''} ${job.LastName ?? ''}`.toLowerCase().includes(filters.AssignedResource.toLowerCase());
      const matchesAuditScore = !filters.AuditScore || (job.OverallScore ?? '').toLowerCase().includes(filters.AuditScore.toLowerCase());
      const matchesLenderName = !filters.LenderName || (job.LenderName ?? '').toLowerCase().includes(filters.LenderName.toLowerCase()); // Updated logic for LenderName
      const matchesSLA = !filters.SLA || job.SLA.toString().includes(filters.SLA); // Add SLA filter logic
      const matchesArea = !filters.Area || (region_lookup[job.AreaId] ?? '').toLowerCase().includes(filters.Area.toLowerCase()); // Add Area filter logic

      return matchesArea && matchesJobRef && matchesAddress && matchesReasonNames && matchesAuditCreation && matchesAssignedResource && matchesAuditScore && matchesSLA;
    });

    setFilteredJobs(filtered);
  }, [filters, auditJobs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    // save filters to local storage
    const newFilters = {
      ...filters,
      [name]: value,
    };
    localStorage.setItem('completedAuditFilters', JSON.stringify(newFilters));
  };

  const handleOpenDialog = (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
  };

  useEffect(() => {
    // load filters from local storage
    const savedFilters = localStorage.getItem('completedAuditFilters');
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);


  const handleConfirm = async () => {
    if (!selectedJob) return;
  
    try {
      const { status, data } = await axiosInstance.get(`/api/check-lock/${selectedJob.JobId}/`);
      if (status === 200 && data.locked) {
        alert('This job is currently locked. You cannot create an audit for this job at this time.');
        setDialogOpen(false);
        setSelectedJob(null);
        window.location.reload(); // refresh the page to update the lock status
        return; // stop here; do not create
      }
    } catch (err) {
      alert('Unable to verify job status. Please try again.');
      return; // be safe and do not proceed
    }
  
    const body = {
      jobId: selectedJob.JobId,
      reasons: selectedJob.ReasonNames,
      resourceId: selectedJob.AllocatedResourceId,
      firstName: selectedJob.FirstName,
      lastName: selectedJob.LastName,
      address: selectedJob.Address,
      areaId: selectedJob.AreaId,
      statusId: selectedJob.StatusId,
      statusName: selectedJob.StatusName,
      jobProduct: selectedJob.JobProduct,
      jobRef: selectedJob.JobRef,
      lenderName: selectedJob.LenderName,
      auditCreationComment: selectedJob.CreationComment,
    };
  
    try {
      const response = await axiosInstance.post(`/api/audit/${selectedJob.JobId}/`, body);
      if (response.status === 201) {
        const audit_id = response.data.audit.audit_id;
        setAuditId(audit_id);
        history.push(`/jobs/audit/${audit_id}`);
        setDialogOpen(false);
      }
    } catch (createErr) {
      alert('Failed to create audit. Please try again.');
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setSelectedJob(null);
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        All jobs with open audits
      </Typography>
      <TableContainer component={Paper} sx={{ opacity: 0.9 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TextField
                    label="Filter CW Ref"
                    variant="outlined"
                    size="small"
                    name="JobRef"
                    value={filters.JobRef}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter Address"
                    variant="outlined"
                    size="small"
                    name="Address"
                    value={filters.Address}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter Reason Names"
                    variant="outlined"
                    size="small"
                    name="ReasonNames"
                    value={filters.ReasonNames}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter Lender"
                    variant="outlined"
                    size="small"
                    name="LenderName"
                    value={filters.LenderName}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter Area"
                    variant="outlined"
                    size="small"
                    name="Area"
                    value={filters.Area}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter Assigned Resource"
                    variant="outlined"
                    size="small"
                    name="AssignedResource"
                    value={filters.AssignedResource}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Filter SLA"
                    variant="outlined"
                    size="small"
                    name="SLA"
                    value={filters.SLA}
                    onChange={handleFilterChange}
                    fullWidth
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>CW Ref</strong></TableCell>
                <TableCell><strong>Address</strong></TableCell>
                <TableCell><strong>Reason Name(s)</strong></TableCell>
                <TableCell><strong>Lender</strong></TableCell>
                <TableCell><strong>Region</strong></TableCell>
                <TableCell><strong>Assigned Resource</strong></TableCell>
                <TableCell><strong>Remaining SLA</strong></TableCell>
                <TableCell><strong></strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job) => (
                  <TableRow key={job.JobId}>
                    <TableCell>{job.JobRef}</TableCell>
                    <TableCell>
                      {`${job.Address?.HouseNumber ?? ''} ${job.Address?.HouseName ?? ''} ${job.Address?.Street ?? ''} ${job.Address?.Town ?? ''} ${job.Address?.Postcode ?? ''}`.trim()}
                    </TableCell>
                    <TableCell>
                      {job.ReasonNames.map((reason, index) => (
                        <span key={index}>
                          {reason}
                          {index < job.ReasonNames.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </TableCell>
                    <TableCell>{job.LenderName}</TableCell>
                    <TableCell>{region_lookup[job.AreaId]}</TableCell>
                    <TableCell>{job.FirstName + ' ' + job.LastName}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-block',
                          borderRadius: '50%',
                          width: '40px', // Fixed width
                          height: '40px', // Fixed height
                          lineHeight: '40px', // Vertically center the text
                          color: 'white',
                          backgroundColor: job.SLA >= 0 ? '#2eb135' : '#e60000', // Green for SLA >= 0, Red for SLA < 0
                          textAlign: 'center',
                          fontSize: '16px', // Adjust font size for better readability
                          fontWeight: 'bold', // Make the text bold
                        }}
                      >
                        {job.SLA}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleOpenDialog(job)}
                        style={{ textDecoration: 'none', color: 'blue' }}
                      >
                        Create Audit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No matches found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          Are you sure you want to create an audit for this job? This action will lock the case to you.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditJobs;
